import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Search, Users as UsersIcon, Laptop, ChevronLeft, ChevronRight, Eye, IdCard,
} from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { EmpleadoDetalleDialog } from '@/components/empleados/EmpleadoDetalleDialog';
import { CarnetEmpleadoDialog } from '@/components/empleados/CarnetEmpleadoDialog';

import { getEmpleados } from '@/services/api/empleados.api';
import { cn, initials } from '@/lib/utils';
import { ReportButton } from '@/components/reportes/ReportButton';

const PAGE_SIZE = 20;

type EstadoFiltro = 'TODOS' | 'ACTIVOS' | 'INACTIVOS';

export function EmpleadosPage() {
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState<EstadoFiltro>('ACTIVOS');
  const [page, setPage] = useState(1);
  const [detalleId, setDetalleId] = useState<string | null>(null);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [carnetId, setCarnetId] = useState<string | null>(null);
  const [carnetOpen, setCarnetOpen] = useState(false);

  const activoParam = estado === 'TODOS' ? undefined : estado === 'ACTIVOS';

  const { data, isLoading } = useQuery({
    queryKey: ['empleados', busqueda, estado, page],
    queryFn: () => getEmpleados({
      busqueda: busqueda.trim() || undefined,
      activo: activoParam,
      page,
      pageSize: PAGE_SIZE,
    }),
    placeholderData: keepPreviousData,
  });

  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  function setFiltro<T>(setter: (v: T) => void) {
    return (v: T) => { setter(v); setPage(1); };
  }

  function verDetalle(id: string) {
    setDetalleId(id);
    setDetalleOpen(true);
  }

  function verCarnet(id: string) {
    setCarnetId(id);
    setCarnetOpen(true);
  }

  return (
    <div className="space-y-6">
      {/* ── Encabezado ──────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-widest text-primary-600">Directorio</p>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">Colaboradores</h2>
          <p className="text-sm text-muted-foreground">
            Consulta el personal, su equipo asignado y su historial de accesos.
          </p>
        </div>
        <ReportButton
          reporte="colaboradores-directorio"
          filtros={{
            busqueda: busqueda || undefined,
            // 'TODOS' no filtra; los otros dos se traducen al booleano del reporte.
            activo: estado === 'TODOS' ? undefined : estado === 'ACTIVOS',
          }}
        />
      </div>

      {/* ── Filtros ─────────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o documento..."
                value={busqueda}
                onChange={(e) => setFiltro(setBusqueda)(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={estado} onValueChange={(v) => setFiltro(setEstado)(v as EstadoFiltro)}>
              <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                <SelectItem value="ACTIVOS">Activos</SelectItem>
                <SelectItem value="INACTIVOS">Inactivos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ── Tabla ───────────────────────────────────────────────────────── */}
      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Colaborador</TableHead>
              <TableHead className="hidden md:table-cell">Cargo</TableHead>
              <TableHead className="hidden lg:table-cell">Área</TableHead>
              <TableHead>Herramientas</TableHead>
              <TableHead className="text-right w-[200px]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && !data ? (
              [...Array(8)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={5}><Skeleton className="h-10 w-full" /></TableCell>
                </TableRow>
              ))
            ) : !data?.items.length ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <UsersIcon className="h-8 w-8 opacity-40" />
                    <p className="text-sm">No hay colaboradores que coincidan con los filtros</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.items.map((e) => (
                <TableRow
                  key={e.id_empleado}
                  className="cursor-pointer"
                  onClick={() => verDetalle(e.id_empleado)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <span className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ring-1',
                        e.activo ? 'bg-primary-50 text-primary-700 ring-primary-100' : 'bg-slate-100 text-slate-500 ring-slate-200',
                      )}>
                        {initials(e.nombres)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">
                          {e.nombres}
                          {!e.activo && <span className="ml-2 text-[10px] font-normal text-muted-foreground">(inactivo)</span>}
                        </p>
                        <p className="text-xs text-muted-foreground tabular">CC {e.documento}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{e.cargo ?? '—'}</TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{e.area ?? '—'}</TableCell>
                  <TableCell>
                    {e.equipos_asignados > 0 ? (
                      <Badge variant="outline" className="gap-1.5 font-normal">
                        <Laptop className="h-3.5 w-3.5" />
                        {e.equipos_asignados}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(ev) => { ev.stopPropagation(); verDetalle(e.id_empleado); }}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        <span className="hidden xl:inline">Ver</span>
                      </Button>
                      {/* El carnet reproduce el QR de portería: solo tiene sentido en personal activo. */}
                      <Button
                        size="sm"
                        variant="outline"
                        title={e.activo ? 'Generar carnet con QR' : 'Colaborador inactivo: sin carnet'}
                        disabled={!e.activo}
                        onClick={(ev) => { ev.stopPropagation(); verCarnet(e.id_empleado); }}
                      >
                        <IdCard className="h-3.5 w-3.5" />
                        <span className="hidden xl:inline">Carnet</span>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {!!data?.total && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <p className="text-xs text-muted-foreground">
              {data.total} empleado{data.total === 1 ? '' : 's'} · Página {page} de {totalPaginas}
            </p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))} disabled={page >= totalPaginas}>
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      <EmpleadoDetalleDialog
        empleadoId={detalleId}
        open={detalleOpen}
        onOpenChange={setDetalleOpen}
        onVerCarnet={verCarnet}
      />

      <CarnetEmpleadoDialog
        empleadoId={carnetId}
        open={carnetOpen}
        onOpenChange={setCarnetOpen}
      />
    </div>
  );
}
